import React, { useEffect, useMemo, useState } from "react";
import Header from "../general/Header";
import Aside from "../general/Aside";
import Footer from "../general/Footer";
import axios from '../../axios/axiosconf';
import Loading from "../general/Loading";
import Unauthorized from "../general/Unauthorized";
import { Link } from "react-router-dom";
import { formatearFecha, formatearMoneda } from "./liquidacionProfesionalPrint";
import ModalLiquidacionHonorarios from "./ModalLiquidacionHonorarios";

const parseAjuste = (valor) => {
    const n = parseFloat(valor);
    return Number.isFinite(n) ? n : 0;
};

const esFacturaNoElectronica = (f) =>
    f.tipo_factura === 'no_electronica' || [4, 5].includes(Number(f.estado));

const estiloBadgeTipoFactura = (f) => {
    const noElect = esFacturaNoElectronica(f);
    return {
        background: noElect ? 'rgba(108, 117, 125, 0.08)' : 'rgba(107, 114, 128, 0.1)',
        border: `1px solid ${noElect ? 'rgba(108, 117, 125, 0.25)' : 'rgba(107, 114, 128, 0.28)'}`,
        color: noElect ? '#6c757d' : '#4b5563',
    };
};

const labelTipoFactura = (f) =>
    esFacturaNoElectronica(f) ? 'No electrónica' : 'Electrónica';

const iconoTipoFactura = (f) =>
    esFacturaNoElectronica(f) ? 'fa-file-alt' : 'fa-bolt';

const tienePermiso = (userdata) => {
    if (userdata.permissions && Array.isArray(userdata.permissions)) {
        return userdata.permissions.some((p) => p.slug === 'verReportes' || p.slug === 'ver_listado_facturas');
    }
    return userdata.up && (userdata.up.includes('verReportes') || userdata.up.includes('ver_listado_facturas'));
};

function ReporteLiquidarProfesional() {
    const [loggedIn, setLoggedIn] = useState(false);
    const [hasPermission, setHasPermission] = useState(false);
    const [client, setClient] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [usuarioId, setUsuarioId] = useState('');
    const [ajuste, setAjuste] = useState('0');
    const [preview, setPreview] = useState(null);
    const [liquidacionGuardada, setLiquidacionGuardada] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [modalExitoVisible, setModalExitoVisible] = useState(false);
    const [modalImpresionVisible, setModalImpresionVisible] = useState(false);

    useEffect(() => {
        if (!localStorage.getItem('userdata')) {
            setHasPermission(false);
            setLoggedIn(true);
            return;
        }
        const userdata = JSON.parse(localStorage.getItem('userdata'));
        if (!tienePermiso(userdata)) {
            setHasPermission(false);
            setLoggedIn(true);
            return;
        }
        setHasPermission(true);

        const hoy = new Date();
        const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        setFechaInicio(inicioMes.toISOString().split('T')[0]);
        setFechaFin(hoy.toISOString().split('T')[0]);

        axios.get('/get-client?uid=' + userdata.user_id)
            .then((r) => { if (r.status === 200) setClient(r.data); setLoggedIn(true); })
            .catch(() => setLoggedIn(true));

        axios.get('/users-by-client?uid=' + userdata.user_id)
            .then((r) => { if (r.status === 200) setUsuarios(r.data || []); })
            .catch(() => setUsuarios([]));
    }, []);

    const ajusteNum = useMemo(() => parseAjuste(ajuste), [ajuste]);
    const formularioBloqueado = !!liquidacionGuardada;

    const resumen = useMemo(() => {
        if (!preview) {
            return {
                valorBruto: 0,
                porcentaje: 0,
                liquidacionBase: 0,
                ajuste: ajusteNum,
                totalPagar: 0,
            };
        }
        const liquidacionBase = Number(preview.valor_liquidacion) || 0;
        return {
            valorBruto: Number(preview.valor_bruto) || 0,
            porcentaje: preview.porcentaje_contratado ?? 0,
            liquidacionBase,
            ajuste: ajusteNum,
            totalPagar: liquidacionBase + ajusteNum,
        };
    }, [preview, ajusteNum]);

    const consultarPreview = () => {
        if (!fechaInicio || !fechaFin || !usuarioId) {
            alert('Seleccione fechas y profesional.');
            return;
        }
        setLoadingPreview(true);
        setErrorMsg('');
        setModalExitoVisible(false);
        setPreview(null);
        setLiquidacionGuardada(null);
        axios.get('/liquidacion-profesional/preview', {
            params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin, users_id: usuarioId },
        })
            .then((r) => setPreview(r.data))
            .catch((err) => {
                setErrorMsg(err.response?.data?.message || 'No se pudo calcular la liquidación.');
            })
            .finally(() => setLoadingPreview(false));
    };

    const aprobarYGuardar = () => {
        if (!preview) {
            alert('Consulte la liquidación antes de aprobar.');
            return;
        }
        if (!window.confirm('¿Confirma aprobar y registrar esta liquidación?')) return;

        setGuardando(true);
        setErrorMsg('');
        setModalExitoVisible(false);
        axios.post('/liquidacion-profesional', {
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin,
            users_id: usuarioId,
            ajuste: ajusteNum,
        })
            .then((r) => {
                const liq = r.data?.liquidacion;
                if (liq) {
                    setLiquidacionGuardada(liq);
                    setModalExitoVisible(true);
                }
            })
            .catch((err) => {
                setErrorMsg(err.response?.data?.message || 'No se pudo guardar la liquidación.');
            })
            .finally(() => setGuardando(false));
    };

    const imprimir = () => {
        if (!liquidacionGuardada) return;
        setModalImpresionVisible(true);
    };

    if (hasPermission === false) return <Unauthorized />;
    if (!loggedIn) return <Loading />;

    return (
        <div className="wrapper hold-transition liq-prof-page" style={{ fontSize: '13px' }}>
            <Header client={client} />
            <Aside client={client} activelink="reports" />
            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-12">
                                <ol className="breadcrumb float-sm-left mb-0">
                                    <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas">Reportes</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas-contables">Contables</Link></li>
                                    <li className="breadcrumb-item"><Link to="/liquidaciones-profesional">Liquidaciones</Link></li>
                                    <li className="breadcrumb-item active">Nueva liquidación</li>
                                </ol>
                            </div>
                        </div>
                        <div className="row mb-2 align-items-end">
                            <div className="col-md-8">
                                <h1 className="m-0">Nueva liquidación</h1>
                                <p className="text-muted mb-0 small">Consulte el borrador, apruebe y luego imprima</p>
                            </div>
                            <div className="col-md-4 text-md-right">
                                <Link to="/liquidaciones-profesional" className="btn btn-outline-secondary btn-sm">
                                    <i className="fas fa-arrow-left mr-1"></i> Volver al listado
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        <div className="card liq-prof-card mb-4">
                            <div className="card-header bg-white border-0 pt-3 pb-0">
                                <h3 className="card-title mb-0 liq-prof-card__title">Parámetros de consulta</h3>
                            </div>
                            <div className="card-body">
                                <div className="row liq-prof-filters">
                                    <div className="col-lg col-md-6 col-sm-6 mb-3 mb-lg-0">
                                        <label htmlFor="fechaInicio">Fecha inicio</label>
                                        <input
                                            id="fechaInicio"
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={fechaInicio}
                                            onChange={(e) => setFechaInicio(e.target.value)}
                                            disabled={formularioBloqueado}
                                        />
                                    </div>
                                    <div className="col-lg col-md-6 col-sm-6 mb-3 mb-lg-0">
                                        <label htmlFor="fechaFin">Fecha fin</label>
                                        <input
                                            id="fechaFin"
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={fechaFin}
                                            onChange={(e) => setFechaFin(e.target.value)}
                                            disabled={formularioBloqueado}
                                        />
                                    </div>
                                    <div className="col-lg col-md-6 col-sm-6 mb-3 mb-lg-0">
                                        <label htmlFor="usuarioId">Profesional</label>
                                        <select
                                            id="usuarioId"
                                            className="form-control form-control-sm"
                                            value={usuarioId}
                                            onChange={(e) => setUsuarioId(e.target.value)}
                                            disabled={formularioBloqueado}
                                        >
                                            <option value="">Seleccione…</option>
                                            {usuarios.map((u) => (
                                                <option key={u.id} value={u.id}>{u.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-lg col-md-6 col-sm-6 mb-3 mb-lg-0">
                                        <label htmlFor="ajuste">Ajuste (+ / −)</label>
                                        <input
                                            id="ajuste"
                                            type="number"
                                            step="0.01"
                                            className="form-control form-control-sm"
                                            value={ajuste}
                                            onChange={(e) => setAjuste(e.target.value)}
                                            placeholder="0"
                                            disabled={formularioBloqueado}
                                        />
                                    </div>
                                    <div className="col-lg col-md-12 col-sm-12 d-flex align-items-end">
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm btn-block liq-prof-btn-consultar"
                                            onClick={consultarPreview}
                                            disabled={loadingPreview || formularioBloqueado}
                                        >
                                            {loadingPreview ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                                    Calculando…
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-search mr-1"></i> Consultar
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                                {errorMsg && <div className="alert alert-danger py-2 mt-3 mb-0 small">{errorMsg}</div>}
                            </div>
                        </div>

                        {preview && (
                            <>
                                <div className="liq-prof-summary mb-4">
                                    <div className="liq-prof-summary__card">
                                        <span className="liq-prof-summary__label">Valor bruto facturado</span>
                                        <strong className="liq-prof-summary__value">{formatearMoneda(resumen.valorBruto)}</strong>
                                    </div>
                                    <div className="liq-prof-summary__card">
                                        <span className="liq-prof-summary__label">% contratado</span>
                                        <strong className="liq-prof-summary__value">{resumen.porcentaje}%</strong>
                                    </div>
                                    <div className="liq-prof-summary__card">
                                        <span className="liq-prof-summary__label">Liquidación base</span>
                                        <strong className="liq-prof-summary__value">{formatearMoneda(resumen.liquidacionBase)}</strong>
                                    </div>
                                    <div className="liq-prof-summary__card">
                                        <span className="liq-prof-summary__label">Ajuste</span>
                                        <strong className="liq-prof-summary__value">{formatearMoneda(resumen.ajuste)}</strong>
                                    </div>
                                    <div className="liq-prof-summary__card liq-prof-summary__card--total">
                                        <span className="liq-prof-summary__label">Total a pagar</span>
                                        <strong className="liq-prof-summary__value">{formatearMoneda(resumen.totalPagar)}</strong>
                                    </div>
                                </div>

                                <div className="card liq-prof-table-card mb-3">
                                    <div className="card-header bg-white border-0 py-3">
                                        <h3 className="card-title mb-0 liq-prof-card__title">
                                            Vista Previa — Facturas del Periodo ({(preview.facturas || []).length})
                                        </h3>
                                    </div>
                                    <div className="card-body p-0">
                                        <div className="table-responsive">
                                            <table className="table liq-prof-table mb-0">
                                                <colgroup>
                                                    <col className="liq-prof-table__col-num" />
                                                    <col className="liq-prof-table__col-tipo" />
                                                    <col className="liq-prof-table__col-pac" />
                                                    <col className="liq-prof-table__col-fecha" />
                                                    <col className="liq-prof-table__col-valor" />
                                                </colgroup>
                                                <thead>
                                                    <tr>
                                                        <th>N° factura</th>
                                                        <th className="text-center">Tipo</th>
                                                        <th>Paciente</th>
                                                        <th className="text-center">Fecha</th>
                                                        <th className="text-right">Valor bruto</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(preview.facturas || []).length ? preview.facturas.map((f, i) => (
                                                        <tr key={f.id} className={i % 2 === 1 ? 'liq-prof-table__zebra' : ''}>
                                                            <td className="liq-prof-table__num">{f.numero || f.id}</td>
                                                            <td className="text-center">
                                                                <span className="badge" style={estiloBadgeTipoFactura(f)}>
                                                                    <i className={`fas ${iconoTipoFactura(f)} mr-1`}></i>
                                                                    {labelTipoFactura(f)}
                                                                </span>
                                                            </td>
                                                            <td className="liq-prof-table__paciente" title={f.paciente_nombre || '—'}>
                                                                {f.paciente_nombre || '—'}
                                                            </td>
                                                            <td className="text-center">{formatearFecha(f.fecha_emision)}</td>
                                                            <td className="text-right">{formatearMoneda(f.valor_bruto)}</td>
                                                        </tr>
                                                    )) : (
                                                        <tr>
                                                            <td colSpan="5" className="text-center text-muted py-4">
                                                                No hay facturas en el periodo
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <div className="d-flex flex-wrap justify-content-end mb-4" style={{ gap: '8px' }}>
                                    {!modalExitoVisible && !liquidacionGuardada && (
                                        <button
                                            type="button"
                                            className="btn btn-success"
                                            onClick={aprobarYGuardar}
                                            disabled={guardando || !(preview.facturas || []).length}
                                        >
                                            <i className="fas fa-check-circle mr-1"></i>
                                            {guardando ? 'Guardando…' : 'Aprobar y Guardar'}
                                        </button>
                                    )}
                                    {!modalExitoVisible && liquidacionGuardada && (
                                        <>
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={imprimir}
                                            >
                                                <i className="fas fa-print mr-1"></i> Imprimir
                                            </button>
                                            <Link to="/liquidaciones-profesional" className="btn btn-outline-secondary">
                                                Ir al listado
                                            </Link>
                                        </>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </section>
            </div>

            {modalExitoVisible && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(15,23,42,0.45)" }}>
                    <div className="modal-dialog modal-dialog-centered modal-sm" role="document">
                        <div className="modal-content text-center" style={{ borderRadius: "12px" }}>
                            <div className="modal-body py-4 px-4">
                                <div className="mb-3">
                                    <i className="fas fa-check-circle text-success" style={{ fontSize: "2.5rem" }}></i>
                                </div>
                                <h5 className="mb-2 font-weight-bold">Liquidación registrada exitosamente</h5>
                                <p className="text-muted small mb-0">
                                    Ya puede imprimir el comprobante de liquidación.
                                </p>
                            </div>
                            <div className="modal-footer justify-content-center py-2 border-0">
                                <button
                                    type="button"
                                    className="btn btn-success btn-sm px-4"
                                    onClick={() => setModalExitoVisible(false)}
                                >
                                    Aceptar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ModalLiquidacionHonorarios
                visible={modalImpresionVisible}
                data={liquidacionGuardada}
                client={client}
                onClose={() => setModalImpresionVisible(false)}
            />

            <Footer />
            <style>{`
                .liq-prof-card {
                    border: none;
                    border-radius: 14px;
                    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
                }
                .liq-prof-card__title {
                    font-size: 0.95rem;
                    font-weight: 700;
                    color: #1f2937;
                }
                .liq-prof-filters label {
                    display: block;
                    font-size: 0.72rem;
                    font-weight: 600;
                    color: #6b7280;
                    text-transform: uppercase;
                    letter-spacing: 0.03em;
                    margin-bottom: 0.35rem;
                }
                .liq-prof-filters .form-control-sm {
                    min-height: 36px;
                    border-radius: 8px;
                }
                .liq-prof-btn-consultar {
                    min-height: 36px;
                    border-radius: 8px;
                    font-weight: 600;
                }
                .liq-prof-summary {
                    display: grid;
                    grid-template-columns: repeat(5, minmax(0, 1fr));
                    gap: 12px;
                }
                @media (max-width: 991px) {
                    .liq-prof-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
                }
                @media (max-width: 575px) {
                    .liq-prof-summary { grid-template-columns: 1fr; }
                }
                .liq-prof-summary__card {
                    background: #fff;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 16px;
                    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
                }
                .liq-prof-summary__card--total {
                    background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%);
                    border-color: #bbf7d0;
                }
                .liq-prof-summary__label {
                    display: block;
                    font-size: 0.72rem;
                    color: #6b7280;
                    margin-bottom: 6px;
                    text-transform: uppercase;
                    letter-spacing: 0.03em;
                }
                .liq-prof-summary__value {
                    display: block;
                    font-size: 1.15rem;
                    color: #111827;
                    line-height: 1.2;
                }
                .liq-prof-summary__card--total .liq-prof-summary__value {
                    font-size: 1.3rem;
                    color: #047857;
                }
                .liq-prof-table-card {
                    border: none;
                    border-radius: 14px;
                    overflow: hidden;
                    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
                }
                .liq-prof-table {
                    table-layout: fixed;
                    width: 100%;
                }
                .liq-prof-table__col-num { width: 16%; min-width: 96px; }
                .liq-prof-table__col-tipo { width: 12%; min-width: 72px; }
                .liq-prof-table__col-pac { width: 38%; min-width: 140px; }
                .liq-prof-table__col-fecha { width: 14%; min-width: 92px; }
                .liq-prof-table__col-valor { width: 16%; min-width: 96px; }
                .liq-prof-table thead th {
                    background: #f8fafc;
                    border-top: none;
                    color: #64748b;
                    font-weight: 600;
                    text-transform: uppercase;
                    font-size: 0.62rem;
                    padding: 11px 12px;
                    letter-spacing: 0.04em;
                }
                .liq-prof-table tbody td {
                    padding: 10px 12px;
                    vertical-align: middle;
                    border-top: 1px solid #f1f5f9;
                    font-size: 0.84rem;
                }
                .liq-prof-table__num {
                    font-weight: 600;
                    color: #111827;
                    white-space: nowrap;
                }
                .liq-prof-table__paciente {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    max-width: 0;
                }
                .liq-prof-table__zebra { background: #f9fafb; }
            `}</style>
        </div>
    );
}

export default ReporteLiquidarProfesional;
